/**
 * @file Technology.tsx
 * @description Technology page explaining Ultimind's Agentic AI architecture.
 */

/**
 * TechnologyPage component outlines how Ultimind's multi-agent system works.
 */
export default function TechnologyPage() {
  const steps = [
    {
      title: '1. Understanding the goal',
      body:
        'The Coordinator Agent receives a natural language request from a user or system and maps it to a graph of subtasks, constraints, and tools.',
    },
    {
      title: '2. Planning & decomposition',
      body:
        'A Planner Agent breaks the goal into role-specific tasks and defines the sequence, dependencies, and stopping criteria for each agent in the network.',
    },
    {
      title: '3. Retrieval & reasoning',
      body:
        'Researcher Agents use retrieval augmented generation to query internal knowledge bases, documents, and external data sources. Results are validated and stored as structured context.',
    },
    {
      title: '4. Execution with tools',
      body:
        'Coder and Operator Agents call tools such as databases, APIs, spreadsheets, or workflow engines to perform actions, calculations, and updates in enterprise systems.',
    },
    {
      title: '5. Governance & memory',
      body:
        'All actions are logged. Short-term and long-term memory modules capture key events, while policy layers enforce security, compliance, and human approvals where required.',
    },
    {
      title: '6. Structured outputs',
      body:
        'Reporter Agents transform the resulting knowledge and state into structured outputs: reports, dashboards, recommendations, and machine-readable payloads.',
    },
  ]

  return (
    <div className="bg-slate-950">
      <section className="mx-auto max-w-5xl px-4 pb-16 pt-10 md:px-6 md:pt-14">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-50 md:text-4xl">
          Technology &amp; Architecture
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-slate-300 md:text-base">
          Ultimind&apos;s Agentic AI framework is designed as a modular,
          observable, and controllable multi-agent environment. It combines
          Large Language Models, retrieval augmented generation, and tool
          execution in a single orchestration layer.
        </p>

        <section className="mt-10 rounded-2xl border border-sky-500/40 bg-slate-900/70 p-5 shadow-[0_0_40px_rgba(56,189,248,0.4)]">
          <h2 className="text-lg font-semibold text-sky-200 md:text-xl">
            Multi-Agent Workflow Diagram
          </h2>
          <p className="mt-3 text-xs text-slate-200 md:text-sm">
            Planner, Researcher, Coder, and Reporter Agents collaborate under a
            Coordinator Agent. Data flows through retrieval, computation, and
            reporting modules, while a governance layer provides observability
            and control.
          </p>
          <div className="mt-5 grid gap-3 text-[11px] text-slate-100 md:grid-cols-3 md:text-xs">
            <div className="rounded-xl bg-slate-800/70 p-3">
              <h3 className="font-semibold text-sky-300">User Request Layer</h3>
              <p className="mt-1 text-slate-200">
                Channels include chat interfaces, workflow triggers, and
                low-code applications.
              </p>
            </div>
            <div className="rounded-xl bg-slate-800/70 p-3">
              <h3 className="font-semibold text-sky-300">Logic Layer</h3>
              <p className="mt-1 text-slate-200">
                Agents coordinate tasks such as &quot;fetch holdings&quot;,
                &quot;calculate returns&quot;, and &quot;generate SoW
                narrative&quot; using LLMs and tools.
              </p>
            </div>
            <div className="rounded-xl bg-slate-800/70 p-3">
              <h3 className="font-semibold text-sky-300">Data &amp; Tools</h3>
              <p className="mt-1 text-slate-200">
                Connectors to document stores, financial data APIs, search
                engines, and internal systems provide rich, verifiable context.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-10 grid gap-6 md:grid-cols-2">
          {steps.map((step) => (
            <article
              key={step.title}
              className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5"
            >
              <h2 className="text-sm font-semibold text-sky-300 md:text-base">
                {step.title}
              </h2>
              <p className="mt-2 text-xs text-slate-300 md:text-sm">
                {step.body}
              </p>
            </article>
          ))}
        </section>
      </section>
    </div>
  )
}
